import React, { createContext, useContext, useEffect, useState } from 'react';
import { clearSession, hasValidSession, isTokenExpired, getToken, setToken } from './session';

interface AuthContextProps {
    isAuthenticated: boolean;
    login: (token: string) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // `hasValidSession` já descarta token vencido, então a aplicação nunca
    // inicia autenticada com credencial morta.
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => hasValidSession());

    const login = (token: string) => {
        setToken(token);
        setIsAuthenticated(true); // Atualiza o estado
    };

    const logout = () => {
        clearSession();
        setIsAuthenticated(false); // Atualiza o estado
    };

    // O token pode vencer com o app aberto. Verifica periodicamente para
    // derrubar a sessão sem esperar a próxima chamada de API responder 401.
    useEffect(() => {
        if (!isAuthenticated) return;

        const interval = setInterval(() => {
            if (isTokenExpired(getToken())) {
                clearSession();
                setIsAuthenticated(false);
            }
        }, 60000);

        return () => clearInterval(interval);
    }, [isAuthenticated]);

    return (
        <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextProps => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
